// @ds/data/auditLogSchema — shared audit-log schema for every entity that
// exposes an audit/activity feed.
//
// Reads from the canonical `entity_audit_log_view` Postgres view. Every
// entity slice filters by `entity_type` + `entity_id` at the consuming
// query layer. The schema owns:
//
//   - resource:   "entity_audit_log_view"
//   - SELECT:     `id, action, actor_id, actor_name, actor_photo, created_at,
//                  before_value, after_value, changed_fields` — the value columns
//                  feed the field-level change feed (delta rows are small; legacy
//                  full-snapshot rows diff client-side via @ds/data/auditChanges)
//   - cache key:  shared across activitySection, EntityAuditMeta, AuditLogPage
//                 → mutations on the view auto-invalidate every consumer.
//
// Pre-G1 this lived in `src/entities/transactions/auditLogSchema.ts`. Promoted
// to `@ds/data/` so the `activitySection()` factory in sharedSections can
// load + render it for ANY entity (invoices, transactions, edpr, …) without
// each entity defining + maintaining a duplicate. Per-entity duplicates remain
// until G2 migrates their consumers.

import type { AuditAction } from "@shared/types/audit";
import { defineSchema } from "@ds/config/schema";
import { TextValue } from "@ds/display/TextValue";

export interface AuditLogRow {
  id: number;
  action: AuditAction;
  actor_id: string | null;
  actor_name: string | null;
  actor_photo: string | null;
  created_at: string;
  before_value: Record<string, unknown> | null;
  after_value: Record<string, unknown> | null;
  changed_fields: string[] | null;
}

export const auditLogSchema = defineSchema<AuditLogRow>({
  resource:   "entity_audit_log_view",
  entityType: "audit_log",
  labels: {
    singular: (t) => t("audit.singular", "Wpis dziennika"),
    plural:   (t) => t("audit.plural",   "Dziennik zdarzeń"),
  },
  permanentFields: [
    "id", "action", "actor_id", "actor_name", "actor_photo", "created_at",
    "before_value", "after_value", "changed_fields",
  ],
})({
  action: {
    labelKey: "audit.action",
    component: TextValue,
    data: (r) => ({ children: r.action }),
    deps: { fields: ["action"] },
    readOnly: true,
  },
});
